import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AppSettingsService from 'services/appSettings';
import StorageService from 'services/StorageService';
import { USER, SESSION } from 'constants/storageConstants';
import { SUPER_ADMIN, isStaffRole } from 'constants/roles';
import { everySection, fallbackSections, normaliseSections } from 'constants/sections';

const storageService = new StorageService();

/**
 * Nothing held: signed out, or not asked yet.
 *  - answer: the last SUCCESSFUL answer, { role, sections }. A failed check never discards it.
 *  - check: the latest COMPLETED check, { pathname, visit, ok } - where the admin was when it was
 *    asked, and whether it succeeded.
 * Both belong to userKey, so what was fetched for one admin is never used for the next.
 */
const NOTHING = { userKey: null, answer: null, check: null };

const SectionAccessContext = createContext(null);

/** Identifies the signed-in admin, so an answer fetched for one admin is never used for the next. */
function currentUserKey() {
    const user = storageService.getValueByKey(USER);

    return user ? String(user.id) : null;
}

function storedRole() {
    return storageService.getValueByKey(USER)?.role ?? null;
}

/**
 * No server answer to go by (none yet, every check so far failed, or no provider): the stored role
 * decides - super admin -> every section, anyone else -> none (fails closed).
 */
function fromStoredRole({ settled, failed }) {
    const role = storedRole();

    return { loaded: false, settled, failed, sections: fallbackSections(role), role };
}

/**
 * Which gated sections (constants/sections) the signed-in admin sees, for the menu and PrivateRoute.
 *
 * Mounted inside the Router: while signed in it asks the server on mount and on every navigation,
 * so a section a super admin opens or locks shows up on the next click, and a new sign-in in the
 * same tab is picked up straight away.
 *
 * Exposes { loaded, settled, failed, sections, role }:
 *  - sections / role: the last successful answer for this admin (so a re-check, or a failed one,
 *    never makes the menu flicker or empty); before any answer, the stored role (fallbackSections).
 *  - loaded: sections / role are a real server answer (not the stored-role fallback).
 *  - settled: the latest completed check was asked on this visit to the current path. A change of
 *    query string only (pagination, search) stays on the same visit, so it does not unsettle it;
 *    moving to another path does, until that path's own check comes back. PrivateRoute waits for
 *    this before opening a gated section, so it never decides with the previous page's answer.
 *  - failed: the latest completed check failed.
 */
export function SectionAccessProvider({ children }) {
    const location = useLocation();
    const [held, setHeld] = useState(NOTHING);
    const latestRequest = useRef(0);
    // Each arrival at a new path is a new visit; a query-string-only change is not.
    const visit = useRef({ pathname: null, id: 0 });

    useEffect(() => {
        const pathname = location.pathname;
        if (visit.current.pathname !== pathname) {
            visit.current = { pathname, id: visit.current.id + 1 };
        }
        const askedOnVisit = visit.current.id;

        latestRequest.current += 1;
        const requestId = latestRequest.current;

        if (!storageService.getValueByKey(SESSION)) {
            setHeld(NOTHING);
            return;
        }

        const forUser = currentUserKey();
        const isStale = () => requestId !== latestRequest.current || currentUserKey() !== forUser;
        const completed = ok => ({ pathname, visit: askedOnVisit, ok });

        AppSettingsService.getVisibleSections()
            .then((response) => {
                if (isStale()) {
                    return;
                }
                const data = response && response.data;
                // The server answers with the effective role: Doctor (1), Admin (4) or Super admin (3).
                if (!data || !isStaffRole(data.role)) {
                    throw new Error('Unexpected visible-sections response');
                }

                // The server's role is the effective one (3 only while the session AND the account
                // are super admin, 4 likewise for admin), so a demoted admin's stored role follows it down.
                const stored = storageService.getValueByKey(USER);
                if (stored && stored.role !== data.role) {
                    storageService.addValueByKey(USER, { ...stored, role: data.role });
                }

                // One update (answer and check together): a promise callback is not batched, and a
                // render with the new check but the old answer would decide with the old answer.
                setHeld({
                    userKey: forUser,
                    answer: {
                        role: data.role,
                        sections: data.role === SUPER_ADMIN ? everySection(true) : normaliseSections(data.sections),
                    },
                    check: completed(true),
                });
            })
            .catch(() => {
                if (isStale()) {
                    return;
                }
                // A failed check (a network blip, a 5xx) keeps the last good answer for this admin.
                setHeld(previous => ({
                    userKey: forUser,
                    answer: previous.userKey === forUser ? previous.answer : null,
                    check: completed(false),
                }));
            });
    }, [location]);

    // Answers that arrive after unmount are ignored.
    useEffect(() => () => { latestRequest.current += 1; }, []);

    const hasSession = Boolean(storageService.getValueByKey(SESSION));
    const isCurrent = hasSession && held.userKey !== null && held.userKey === currentUserKey();
    const answer = isCurrent ? held.answer : null;
    const check = isCurrent ? held.check : null;
    // Signed out: nothing is asked, so there is nothing to wait for.
    const settled = !hasSession || (
        Boolean(check) && check.pathname === location.pathname && check.visit === visit.current.id
    );
    const failed = Boolean(check) && !check.ok;

    const value = answer
        ? { loaded: true, settled, failed, sections: answer.sections, role: answer.role }
        : fromStoredRole({ settled, failed });

    return (
        <SectionAccessContext.Provider value={value}>
            {children}
        </SectionAccessContext.Provider>
    );
}

/**
 * The section access in force. Outside a provider nothing is ever asked, so there is nothing to wait
 * for: it goes by the stored role (fails closed).
 */
export function useSectionAccess() {
    const value = useContext(SectionAccessContext);

    return value || fromStoredRole({ settled: true, failed: false });
}

export default SectionAccessContext;
